import type { FactStats, Operation, Presentation, Question, Settings } from './model';
import { makeId } from './model';

type Random = () => number;

export const MENU_FOODS = [
  'chocolate-chip cookies',
  'jam thumbprints',
  'sprinkle cookies',
  'star cookies',
  'gingerbread cats',
  'vanilla rings',
  'cocoa crinkles',
  'lemon buttons',
  'berry tarts',
  'mini muffins',
  'cinnamon swirls',
  'pink macarons',
  'frosted cupcakes',
  'butter croissants',
  'cream puffs',
  'chocolate éclairs',
] as const;

export type MenuFood = typeof MENU_FOODS[number];

function pick<T>(items: readonly T[], rng: Random): T {
  return items[Math.floor(rng() * items.length)]!;
}

function weightedPick<T>(items: T[], weight: (item: T) => number, rng: Random): T {
  const weights = items.map((item) => Math.max(0.05, weight(item)));
  const total = weights.reduce((sum, n) => sum + n, 0);
  let cursor = rng() * total;
  for (let i = 0; i < items.length; i += 1) {
    cursor -= weights[i]!;
    if (cursor <= 0) return items[i]!;
  }
  return items.at(-1)!;
}

export function equationFor(operation: Operation, a: number, b: number, presentation: Presentation): string {
  const symbol = { add: '+', subtract: '−', multiply: '×', divide: '÷' }[operation];
  if (presentation === 'missing') return `${a} ${symbol} ? = ${operation === 'add' ? a + b : operation === 'subtract' ? a - b : operation === 'multiply' ? a * b : a / b}`;
  return `${a} ${symbol} ${b} = ?`;
}

function storyFor(operation: Operation, a: number, b: number, food: string, presentation: Presentation): string {
  if (presentation === 'missing') {
    if (operation === 'add') return `${a} ${food}, then some more. How many more?`;
    if (operation === 'subtract') return `${a} ${food}. Some are served. How many were served?`;
    if (operation === 'multiply') return `${a} plates make the total. How many ${food} on each?`;
    return `${a} ${food} shared equally. How many plates?`;
  }
  if (operation === 'add') return `${a} ${food}, then ${b} more. How many?`;
  if (operation === 'subtract') return `${a} ${food}. Serve ${b}. How many are left?`;
  if (operation === 'multiply') return `${a} plates, ${b} on each. How many ${food}?`;
  return `Share ${a} ${food} onto ${b} plates. How many on each?`;
}

export function chooseMenuFood(previousFood?: string, rng: Random = Math.random): MenuFood {
  const choices = previousFood ? MENU_FOODS.filter((food) => food !== previousFood) : MENU_FOODS;
  return pick(choices.length ? choices : MENU_FOODS, rng);
}

function createQuestion(operation: Operation, a: number, b: number, presentation: Presentation, rng: Random, previousFood?: string): Question {
  let answer: number;
  if (presentation === 'missing') answer = b;
  else answer = operation === 'add' ? a + b : operation === 'subtract' ? a - b : operation === 'multiply' ? a * b : a / b;
  const food = chooseMenuFood(previousFood, rng);
  const factKey = `${operation}:${a}:${b}`;
  return { id: makeId('q'), operation, a, b, answer, presentation, equation: equationFor(operation, a, b, presentation), story: storyFor(operation, a, b, food, presentation), food, factKey };
}

function allFacts(operation: Operation, settings: Settings): Array<[number, number]> {
  const facts: Array<[number, number]> = [];
  if (operation === 'add') {
    for (let a = 0; a <= settings.addLimit; a += 1) for (let b = 0; b <= settings.addLimit - a; b += 1) facts.push([a, b]);
  } else if (operation === 'subtract') {
    for (let a = 0; a <= settings.subtractLimit; a += 1) for (let b = 0; b <= a; b += 1) facts.push([a, b]);
  } else if (operation === 'multiply') {
    for (const table of settings.multiplicationTables) for (let n = 0; n <= settings.factorMax; n += 1) facts.push([table, n]);
  } else {
    for (const divisor of settings.divisionFamilies.filter((n) => n > 0)) for (let quotient = 0; quotient <= settings.factorMax; quotient += 1) facts.push([divisor * quotient, divisor]);
  }
  return facts;
}

function answerFor(operation: Operation, a: number, b: number): number {
  return operation === 'add' ? a + b : operation === 'subtract' ? a - b : operation === 'multiply' ? a * b : a / b;
}

export function buildOperationPlan(operations: Operation[], count = 10, rng: Random = Math.random): Operation[] {
  const plan: Operation[] = [];
  while (plan.length < count) plan.push(...operations);
  plan.length = count;
  for (let i = plan.length - 1; i > 1; i -= 1) {
    const j = 2 + Math.floor(rng() * (i - 1));
    [plan[i], plan[j]] = [plan[j]!, plan[i]!];
  }
  return plan;
}

export function generateQuestion(operation: Operation, settings: Settings, stats: Record<string, FactStats>, seen: string[] = [], allowPuzzle = false, rng: Random = Math.random, previousFood?: string): Question {
  const pool = allFacts(operation, settings).filter(([a, b]) => !seen.includes(`${operation}:${a}:${b}`));
  const source = pool.length ? pool : allFacts(operation, settings);
  const [a, b] = weightedPick(source, ([x, y]) => {
    const prefix = `${operation}:${x}:${y}|`;
    const matching = Object.entries(stats).filter(([key]) => key.startsWith(prefix)).map(([, value]) => value);
    if (!matching.length) return 5;
    const recent = matching.flatMap((s) => s.recent).slice(-5);
    const misses = recent.filter((v) => !v).length;
    return 1.4 + misses * 1.25;
  }, rng);
  const rawAnswer = answerFor(operation, a, b);
  const safePuzzle = allowPuzzle && b > 0 && !(operation === 'multiply' && a === 0) && !(operation === 'divide' && rawAnswer === 0);
  return createQuestion(operation, a, b, safePuzzle ? 'missing' : 'result', rng, previousFood);
}

export function avoidRepeatedMenuFood(question: Question, previousFood?: string, rng: Random = Math.random): Question {
  if (!previousFood || question.food !== previousFood) return question;
  const food = chooseMenuFood(previousFood, rng);
  return { ...question, food, story: storyFor(question.operation, question.a, question.b, food, question.presentation) };
}

export function choiceAnswers(question: Question, rng: Random = Math.random): number[] {
  const correct = question.answer;
  const options = new Set<number>([correct]);
  const candidates = [correct - 1, correct + 1, correct - 2, correct + 2, correct - question.b, correct + question.b, question.a, question.b].filter((n) => n >= 0 && Number.isInteger(n));
  for (let guard = 0; options.size < 3 && guard < 40; guard += 1) {
    const value = candidates.length ? candidates.splice(Math.floor(rng() * candidates.length), 1)[0]! : Math.max(0, correct + Math.floor(rng() * 9) - 4);
    options.add(value);
  }
  const result = [...options].slice(0, 3);
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [result[i], result[j]] = [result[j]!, result[i]!];
  }
  return result;
}

export function explanationFor(q: Question): string {
  if (q.operation === 'add') return `Put ${q.a} and ${q.b} together. ${q.a} + ${q.b} = ${q.a + q.b}.`;
  if (q.operation === 'subtract') return `Start with ${q.a}. Take away ${q.b}. ${q.a - q.b} remain.`;
  if (q.operation === 'multiply') return `${q.a} equal groups with ${q.b} in each make ${q.a * q.b}.`;
  return `Share ${q.a} equally among ${q.b} plates. Each plate gets ${q.a / q.b}.`;
}
